import { useMemo, useState } from 'react';

import { Pie } from '@ant-design/charts';
import { Card, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';

import { VISIT_COLORS } from 'scenes/VisitHistory/utils/charts';
import { complianceLevel } from 'scenes/VisitHistory/utils/compliance';
import { formatDecimal, formatInt } from 'scenes/VisitHistory/utils/format';

import { CommuneSummary } from '../utils/storeVisits';

const I18N_BASE = 'scenes.store_visits.communes';
const CSS = 'store-visits-communes';
const PIE_COLORS = [
  '#012f86',
  '#00bfb3',
  '#f59e0b',
  '#64748b',
  '#8b5cf6',
  '#cbd5e1',
];

type CommuneView = 'bars' | 'pie';

interface StoreVisitsCommunesProps {
  summary: CommuneSummary;
}

/** Top 12 comunas por locales planificados, o torta top 5 + otras. */
function StoreVisitsCommunes({ summary }: StoreVisitsCommunesProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [view, setView] = useState<CommuneView>('bars');
  const int = (n: number) => formatInt(n, language);
  const pct = (n: number) => `${formatDecimal(n, language)}%`;
  const max = Math.max(1, ...summary.top.map((row) => row.planned));
  const otherLabel = t(`${I18N_BASE}.other`, {
    count: Math.max(0, summary.communes - 5),
  });

  const pieData = useMemo(
    () =>
      summary.pie.map((row) => ({
        ...row,
        label: row.key === '__other__' ? otherLabel : row.label,
      })),
    [summary.pie, otherLabel]
  );

  const bar = (executed: number, incidents: number) => (
    <span className={`${CSS}__track`}>
      <span
        style={{
          width: `${(executed / max) * 100}%`,
          background: VISIT_COLORS.executed,
        }}
      />
      <span
        style={{
          width: `${(incidents / max) * 100}%`,
          background: VISIT_COLORS.incident,
        }}
      />
    </span>
  );

  return (
    <Card
      className={CSS}
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          <span className="visit-history-inc__sub">
            {t(`${I18N_BASE}.subtitle`, { count: summary.communes })}
          </span>
        </>
      }
      extra={
        <Segmented
          size="small"
          value={view}
          onChange={(value) => setView(value as CommuneView)}
          options={[
            { value: 'bars', label: t(`${I18N_BASE}.views.bars`) },
            { value: 'pie', label: t(`${I18N_BASE}.views.pie`) },
          ]}
        />
      }
    >
      {summary.communes === 0 ? (
        <div className="visit-history-inc__empty">
          {t(`${I18N_BASE}.empty`)}
        </div>
      ) : view === 'bars' ? (
        <>
          <div className={`${CSS}__head`}>
            <span>{t(`${I18N_BASE}.bars_hint`)}</span>
            <span className="visit-history-legend">
              <span>
                <i style={{ background: VISIT_COLORS.executed }} />
                {t(`${I18N_BASE}.legend.executed`)}
              </span>
              <span>
                <i style={{ background: VISIT_COLORS.incident }} />
                {t(`${I18N_BASE}.legend.incidents`)}
              </span>
            </span>
          </div>
          <ul className={`${CSS}__list`}>
            {summary.top.map((row) => (
              <li key={row.key} className={`${CSS}__row`}>
                <span className={`${CSS}__name`} title={row.label}>
                  {row.label}
                </span>
                {bar(row.executed, row.incidents)}
                <span className={`${CSS}__count`}>
                  {`${int(row.executed)} / ${int(row.planned)}`}
                </span>
                <span
                  className={`${CSS}__pct store-visits-compliance__pct--${complianceLevel(
                    row.compliancePct
                  )}`}
                >
                  {pct(row.compliancePct)}
                </span>
              </li>
            ))}
            {summary.other && (
              <li className={`${CSS}__row ${CSS}__row--other`}>
                <span className={`${CSS}__name`}>
                  {t(`${I18N_BASE}.others`, { count: summary.other.communes })}
                </span>
                <span className={`${CSS}__muted`}>
                  {t(`${I18N_BASE}.stores`, {
                    count: summary.other.planned,
                    stores: int(summary.other.planned),
                  })}
                </span>
                <span className={`${CSS}__count`}>
                  {`${int(summary.other.executed)} / ${int(
                    summary.other.planned
                  )}`}
                </span>
                <span
                  className={`${CSS}__pct store-visits-compliance__pct--${complianceLevel(
                    summary.other.compliancePct
                  )}`}
                >
                  {pct(summary.other.compliancePct)}
                </span>
              </li>
            )}
          </ul>
        </>
      ) : (
        <div className={`${CSS}__pie`}>
          <Pie
            data={pieData}
            angleField="planned"
            colorField="label"
            radius={1}
            innerRadius={0.62}
            height={260}
            scale={{ color: { range: PIE_COLORS } }}
            legend={false}
            label={false}
            tooltip={{
              title: 'label',
              items: [
                (d: any) => ({
                  name: t(`${I18N_BASE}.legend.planned`),
                  value: `${int(d.planned)} · ${d.pct}%`,
                }),
              ],
            }}
            annotations={[
              {
                type: 'text',
                style: {
                  text: `${int(summary.planned)}\n${t(
                    `${I18N_BASE}.stores_label`
                  )}`,
                  x: '50%',
                  y: '50%',
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 700,
                },
              },
            ]}
          />
          <ul className={`${CSS}__pie-legend`}>
            {pieData.map((row, index) => (
              <li key={row.key}>
                <i
                  style={{ background: PIE_COLORS[index % PIE_COLORS.length] }}
                />
                <span className={`${CSS}__name`}>{row.label}</span>
                <strong>{int(row.planned)}</strong>
                <span className={`${CSS}__muted`}>{`· ${row.pct}%`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={`${CSS}__note`}>
        {t(`${I18N_BASE}.note`)}
        {summary.withoutCommune > 0 &&
          ` ${t(`${I18N_BASE}.without_commune`, {
            count: summary.withoutCommune,
          })}`}
      </div>
    </Card>
  );
}

export default StoreVisitsCommunes;
